import React, { useState, useRef, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { ChevronDown, ChevronRight, Sparkles, Shield, Gem, Circle } from 'lucide-react';
import { getOptimizedImageUrl } from '../../utils/image';

// Organized Category Structure with Subcategories
export const CATEGORY_STRUCTURE = [
  {
    id: 'gemstones',
    name: 'Gemstones',
    handle: 'gemstones',
    icon: '💎',
    color: 'from-blue-500 to-purple-600',
    tagline: 'Authentic & Certified Gemstones',
    subcategories: [
      { handle: 'pukhraj-gemstone', name: 'Pukhraj (Yellow Sapphire)', icon: '🌟', image: 'https://static.prod-images.emergentagent.com/jobs/692a43eb-e119-4d12-81b8-c20badf58ae0/images/c4a89f181edc2cb719c223bbc43a5b34c974a7ba5614d7a3e281d19819437ff8.png' },
      { handle: 'neelam-gemstone', name: 'Neelam (Blue Sapphire)', icon: '💙', image: 'https://static.prod-images.emergentagent.com/jobs/692a43eb-e119-4d12-81b8-c20badf58ae0/images/4810d1ed2591f5c08bdec00cedca26a8e06ebfa96605ec8a504aa82ebb303b0c.png' },
      { handle: 'panna-gemstone', name: 'Panna (Emerald)', icon: '💚', image: 'https://static.prod-images.emergentagent.com/jobs/692a43eb-e119-4d12-81b8-c20badf58ae0/images/3ae0e50870ba68567280097c3c04248277dd018c8cb132762bac1841940c2f4d.png' },
      { handle: 'ruby-gemstone', name: 'Manik (Ruby)', icon: '❤️', image: 'https://static.prod-images.emergentagent.com/jobs/692a43eb-e119-4d12-81b8-c20badf58ae0/images/e7b15b9749e7fff953e6793a33cb927112a853edbddfec88d19082b6b4240bec.png' },
      { handle: 'moonga-gemstone', name: 'Moonga (Red Coral)', icon: '🔴', image: 'https://static.prod-images.emergentagent.com/jobs/692a43eb-e119-4d12-81b8-c20badf58ae0/images/d8eb92b8cb783a659c1f6ee3667c7e73b56f84db7b58ec5cd380630b75054ac0.png' },
      { handle: 'moti-gemstone', name: 'Moti (Pearl)', icon: '⚪', image: 'https://static.prod-images.emergentagent.com/jobs/692a43eb-e119-4d12-81b8-c20badf58ae0/images/45b2703df0cea17fb8bd2150cc9e2ff6b254429bd249f9b89a728b0309114d0c.png' },
      { handle: 'gomeda-gemstone', name: 'Gomed (Hessonite)', icon: '🟤', image: 'https://static.prod-images.emergentagent.com/jobs/692a43eb-e119-4d12-81b8-c20badf58ae0/images/efc5702bdb7a0b458d109a9a7a1a75b45cbcbb9532ed1d68959df2d8acd19321.png' },
      { handle: 'cats-eye-gemstone', name: 'Lehsunia (Cat\'s Eye)', icon: '🟡', image: 'https://static.prod-images.emergentagent.com/jobs/692a43eb-e119-4d12-81b8-c20badf58ae0/images/d07ef7e078dc683f85f9f41e195e069d7e773b021e811ee684ed7b08b23888bc.png' },
      { handle: 'opal-gemstone', name: 'Opal', icon: '🌈', image: 'https://static.prod-images.emergentagent.com/jobs/692a43eb-e119-4d12-81b8-c20badf58ae0/images/14aeb7a563e56ec8a4e26c322bbd68aadcae7cb04fe6fb064c3a574b11ae0280.png' },
    ],
  },
  {
    id: 'rudraksha',
    name: 'Rudraksha',
    handle: 'rudraksha',
    icon: '📿',
    color: 'from-amber-600 to-orange-600',
    tagline: 'Sacred Rudraksha Beads',
    subcategories: [
      { handle: '1-mukhi-rudraksha', name: '1 Mukhi Rudraksha', icon: '1️⃣' },
      { handle: '2-mukhi-rudraksha', name: '2 Mukhi Rudraksha', icon: '2️⃣' },
      { handle: '3-mukhi-rudraksha', name: '3 Mukhi Rudraksha', icon: '3️⃣' },
      { handle: '4-mukhi-rudraksha', name: '4 Mukhi Rudraksha', icon: '4️⃣' },
      { handle: '5-mukhi-rudraksha', name: '5 Mukhi Rudraksha', icon: '5️⃣' },
      { handle: 'gauri-shankar-rudraksha', name: 'Gauri Shankar', icon: '🔯' },
      { handle: 'ganesh-rudraksha', name: 'Ganesh Rudraksha', icon: '🐘' },
    ],
  },
  {
    id: 'bracelets',
    name: 'Bracelets',
    handle: 'bracelets',
    icon: '📿',
    color: 'from-purple-500 to-pink-600',
    tagline: 'Healing Crystal Bracelets',
    subcategories: [],
  },
  {
    id: 'yantras',
    name: 'Yantras',
    handle: 'yantras',
    icon: '🔯',
    color: 'from-yellow-500 to-amber-600',
    tagline: 'Powerful Vedic Yantras',
    subcategories: [],
  },
  {
    id: 'crystal-trees',
    name: 'Crystal Trees',
    handle: 'crystal-trees',
    icon: '🌳',
    color: 'from-green-500 to-emerald-600',
    tagline: 'Feng Shui Crystal Trees',
    subcategories: [],
  },
  {
    id: 'malas',
    name: 'Malas',
    handle: 'malas',
    icon: '🙏',
    color: 'from-red-500 to-rose-600',
    tagline: 'Prayer & Meditation Malas',
    subcategories: [],
  },
  {
    id: 'remedies',
    name: 'Vastu Remedies',
    handle: 'vedic-astrologic-remedies',
    icon: '✨',
    color: 'from-indigo-500 to-blue-600',
    tagline: 'Vastu Correction Products',
    subcategories: [
      { handle: '3d-remedies', name: '3D Remedies', icon: '🔮' },
      { handle: 'treatment-remedies', name: 'Treatment Remedies', icon: '💊' },
      { handle: 'negativity-removal-remedies', name: 'Negativity Removal', icon: '🛡️' },
      { handle: 'devta-spray', name: 'Devta Spray', icon: '💨' },
      { handle: 'directional-spray', name: 'Directional Spray', icon: '🧭' },
      { handle: 'divine-oils', name: 'Divine Oils', icon: '🫒' },
      { handle: 'divine-candles', name: 'Divine Candles', icon: '🕯️' },
    ],
  },
  {
    id: 'decor',
    name: 'Spiritual Decor',
    handle: 'paintings',
    icon: '🖼️',
    color: 'from-teal-500 to-cyan-600',
    tagline: 'Sacred Home Decor',
    subcategories: [
      { handle: 'paintings', name: 'Paintings', icon: '🎨' },
      { handle: 'pyrite-frames', name: 'Pyrite Frames', icon: '🪟' },
      { handle: 'devta-divs', name: 'Devta Divs', icon: '🏛️' },
    ],
  },
];

// Category Bar Component - Prominent nav above banner with full-width dropdown panel
export const CategoryBar = () => {
  const [activeCategory, setActiveCategory] = useState(null);
  const barRef = useRef(null);

  const toggleCategory = (id) => {
    setActiveCategory((prev) => (prev === id ? null : id));
  };

  // Close dropdown when clicking outside the bar
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (barRef.current && !barRef.current.contains(e.target)) {
        setActiveCategory(null);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const active = CATEGORY_STRUCTURE.find((c) => c.id === activeCategory && c.subcategories.length > 0);

  return (
    <div
      className="bg-gradient-to-b from-amber-50 to-white border-b border-amber-200 relative z-50 shadow-sm"
      data-testid="category-bar"
      ref={barRef}
    >
      <div className="container mx-auto px-4">
        {/* Scrollable trigger row */}
        <div className="flex items-center justify-start lg:justify-center gap-2 lg:gap-3 py-3 overflow-x-auto scrollbar-hide">
          {CATEGORY_STRUCTURE.map((category) => {
            const hasSubs = category.subcategories.length > 0;
            const isActive = activeCategory === category.id;
            const pillClass = `flex items-center gap-2 px-4 py-2.5 rounded-full text-sm lg:text-base font-semibold whitespace-nowrap transition-all border ${
              isActive
                ? 'bg-gradient-to-r ' + category.color + ' text-white border-transparent shadow-md scale-105'
                : 'text-foreground bg-white border-amber-100 hover:border-amber-300 hover:shadow-sm'
            }`;

            return hasSubs ? (
              <button
                key={category.id}
                type="button"
                onClick={() => toggleCategory(category.id)}
                aria-expanded={isActive}
                className={pillClass}
                data-testid={`category-trigger-${category.id}`}
              >
                <span className="text-lg">{category.icon}</span>
                <span>{category.name}</span>
                <ChevronDown className={`w-4 h-4 transition-transform ${isActive ? 'rotate-180' : ''}`} />
              </button>
            ) : (
              <Link
                key={category.id}
                to={`/products?category=${category.handle}`}
                className={pillClass}
                data-testid={`category-link-${category.id}`}
              >
                <span className="text-lg">{category.icon}</span>
                <span>{category.name}</span>
              </Link>
            );
          })}
        </div>
      </div>

      {/* Full-width dropdown panel - rendered OUTSIDE the scroll container so it is never clipped */}
      <AnimatePresence>
        {active && (
          <motion.div
            initial={{ opacity: 0, y: -8 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -8 }}
            transition={{ duration: 0.18 }}
            className="absolute top-full left-0 right-0 bg-white border-t border-amber-100 shadow-2xl z-50"
            data-testid={`category-dropdown-${active.id}`}
          >
            <div className="container mx-auto px-4 py-6">
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-2">
                  <span className="text-xl">{active.icon}</span>
                  <h3 className="font-serif text-lg font-bold text-foreground">{active.name}</h3>
                  <span className="text-sm text-muted-foreground hidden sm:inline">— {active.tagline}</span>
                </div>
                <Link
                  to={`/products?category=${active.handle}`}
                  onClick={() => setActiveCategory(null)}
                  className="flex items-center gap-1 text-sm font-semibold text-gold hover:underline whitespace-nowrap"
                >
                  View All {active.name} <ChevronRight className="w-4 h-4" />
                </Link>
              </div>
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-2">
                {active.subcategories.map((sub) => (
                  <Link
                    key={sub.handle}
                    to={`/products?category=${sub.handle}`}
                    onClick={() => setActiveCategory(null)}
                    className="flex items-center gap-2 px-3 py-2.5 rounded-lg border border-amber-50 hover:border-amber-300 hover:bg-amber-50 text-sm text-foreground transition-all"
                    data-testid={`subcategory-${sub.handle}`}
                  >
                    <span className="text-lg">{sub.icon}</span>
                    <span className="font-medium leading-tight">{sub.name}</span>
                  </Link>
                ))}
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

// Featured Gemstones Section for Homepage
export const FeaturedGemstones = () => {
  const gemstones = CATEGORY_STRUCTURE.find(c => c.id === 'gemstones')?.subcategories || [];

  return (
    <section className="py-12 lg:py-16 bg-gradient-to-b from-white to-blue-50/30" data-testid="featured-gemstones">
      <div className="container mx-auto px-4 lg:px-8">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-center mb-10"
        >
          <div className="inline-flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-blue-500 to-purple-600 text-white rounded-full mb-4">
            <Gem className="w-5 h-5" />
            <span className="font-bold text-sm">CERTIFIED GEMSTONES</span>
            <Shield className="w-5 h-5" />
          </div>
          <h2 className="font-serif text-2xl lg:text-4xl font-bold text-foreground mb-3">
            Shop Authentic & <span className="text-transparent bg-clip-text bg-gradient-to-r from-blue-500 to-purple-600">Certified Gemstones</span>
          </h2>
          <p className="text-muted-foreground max-w-2xl mx-auto">
            Lab-certified, energized gemstones for astrological benefits. Each stone is carefully selected and blessed for maximum positive effects.
          </p>
        </motion.div>

        {/* Gemstone Grid */}
        <div className="grid grid-cols-3 md:grid-cols-5 lg:grid-cols-9 gap-3 lg:gap-4">
          {gemstones.map((gem, index) => (
            <motion.div
              key={gem.handle}
              initial={{ opacity: 0, scale: 0.9 }}
              whileInView={{ opacity: 1, scale: 1 }}
              viewport={{ once: true }}
              transition={{ delay: index * 0.05 }}
            >
              <Link
                to={`/products?category=${gem.handle}`}
                className="group flex flex-col items-center p-3 lg:p-4 bg-white rounded-xl border border-blue-100 hover:border-blue-300 hover:shadow-lg transition-all text-center"
              >
                <div className="w-16 h-16 lg:w-20 lg:h-20 rounded-full bg-gradient-to-br from-blue-50 to-purple-50 flex items-center justify-center mb-2 overflow-hidden ring-1 ring-blue-100 group-hover:scale-110 group-hover:ring-blue-300 transition-all">
                  {gem.image ? (
                    <img
                      src={getOptimizedImageUrl(gem.image, 200)}
                      alt={gem.name}
                      loading="lazy"
                      className="w-full h-full object-contain p-1.5"
                    />
                  ) : (
                    <span className="text-2xl lg:text-3xl">{gem.icon}</span>
                  )}
                </div>
                <span className="text-xs lg:text-sm font-medium text-foreground line-clamp-2">
                  {gem.name.split('(')[0].trim()}
                </span>
                {gem.name.includes('(') && (
                  <span className="text-[10px] lg:text-xs text-muted-foreground">
                    ({gem.name.split('(')[1]}
                  </span>
                )}
              </Link>
            </motion.div>
          ))}
        </div>

        {/* View All Button */}
        <div className="text-center mt-8">
          <Link
            to="/products?category=gemstones"
            className="inline-flex items-center gap-2 px-6 py-3 bg-gradient-to-r from-blue-500 to-purple-600 text-white rounded-full font-medium hover:shadow-lg transition-all"
          >
            <Gem className="w-5 h-5" />
            View All Gemstones
            <ChevronRight className="w-5 h-5" />
          </Link>
        </div>
      </div>
    </section>
  );
};

export default CategoryBar;
